import { Download, Printer } from "lucide-react";
import type { FinancingResult } from "../../shared/contracts";
import { annualizeSchedule } from "../lib/finance";
import {
  currencyDetailed,
  downloadText,
  scheduleToCsv,
  systemLabel
} from "../lib/format";
import { BalanceChart } from "./BalanceChart";

interface ScheduleSectionProps {
  result: FinancingResult;
  view: "annual" | "monthly";
  onViewChange: (view: "annual" | "monthly") => void;
}

export function ScheduleSection({ result, view, onViewChange }: ScheduleSectionProps) {
  const rows = view === "annual" ? annualizeSchedule(result.schedule) : result.schedule;
  const visibleRows = view === "annual" ? rows : rows.slice(0, 36);

  function exportCsv() {
    const source = view === "annual" ? annualizeSchedule(result.schedule) : result.schedule;
    downloadText(
      `alicerce-${result.bankId}-${result.system}-${view}.csv`,
      `\uFEFF${scheduleToCsv(source, view === "annual")}`,
      "text/csv;charset=utf-8"
    );
  }

  return (
    <section className="schedule-section" id="cronograma" aria-labelledby="schedule-title">
      <div className="schedule-section__heading">
        <div>
          <span className="section-kicker">05 · Cronograma auditável</span>
          <h2 id="schedule-title">
            {result.bankName} · {systemLabel(result.system)}
          </h2>
          <p>
            {result.annualRate.toFixed(2).replace(".", ",")}% a.a. efetivos ·{" "}
            {result.schedule.length} parcelas
          </p>
        </div>
        <div className="schedule-actions">
          <button className="button button--soft" type="button" onClick={exportCsv}>
            <Download size={16} aria-hidden="true" /> CSV
          </button>
          <button className="button button--soft" type="button" onClick={() => window.print()}>
            <Printer size={16} aria-hidden="true" /> Imprimir / PDF
          </button>
        </div>
      </div>

      {!result.eligibility.eligible && (
        <div className="eligibility-note" role="note">
          <strong>Esta opção ficou fora dos critérios informados.</strong>
          <ul>
            {result.eligibility.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          <span>
            Renda mínima estimada para esta parcela:{" "}
            {currencyDetailed(result.eligibility.minimumIncome)}
          </span>
        </div>
      )}

      <div className="schedule-grid">
        <BalanceChart schedule={result.schedule} accent={result.accent} />
        <dl className="cost-breakdown">
          <div>
            <dt>Valor financiado</dt>
            <dd>{currencyDetailed(result.financedAmount)}</dd>
          </div>
          <div>
            <dt>Juros</dt>
            <dd>{currencyDetailed(result.totalInterest)}</dd>
          </div>
          <div>
            <dt>Seguros estimados</dt>
            <dd>{currencyDetailed(result.totalInsurance)}</dd>
          </div>
          <div>
            <dt>Tarifas mensais</dt>
            <dd>{currencyDetailed(result.totalAdminFees)}</dd>
          </div>
          <div>
            <dt>Tarifa inicial</dt>
            <dd>{currencyDetailed(result.upfrontFee)}</dd>
          </div>
          {Math.abs(result.roundingAdjustment) >= 0.005 && (
            <div>
              <dt>Ajuste de arredondamento</dt>
              <dd>{currencyDetailed(result.roundingAdjustment)}</dd>
            </div>
          )}
          <div className="cost-breakdown__total">
            <dt>Total estimado</dt>
            <dd>{currencyDetailed(result.totalPaid)}</dd>
          </div>
        </dl>
      </div>

      <div className="schedule-table__controls">
        <div className="segmented-control" aria-label="Periodicidade do cronograma">
          <button
            type="button"
            aria-pressed={view === "annual"}
            onClick={() => onViewChange("annual")}
          >
            Visão anual
          </button>
          <button
            type="button"
            aria-pressed={view === "monthly"}
            onClick={() => onViewChange("monthly")}
          >
            Visão mensal
          </button>
        </div>
        {view === "monthly" && result.schedule.length > 36 && (
          <small>Exibindo as 36 primeiras parcelas. O CSV inclui todas.</small>
        )}
      </div>

      <div
        className="table-scroll schedule-table"
        tabIndex={0}
        aria-label={`Cronograma ${view === "annual" ? "anual" : "mensal"}; deslize horizontalmente para ver todas as colunas`}
      >
        <table>
          <thead>
            <tr>
              <th>{view === "annual" ? "Ano" : "Mês"}</th>
              <th>Saldo inicial</th>
              <th>Amortização</th>
              <th>Juros</th>
              <th>Seguro + tarifa</th>
              <th>Pagamento</th>
              <th>Saldo final</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((item) => (
              <tr key={item.month}>
                <td>{item.month}</td>
                <td>{currencyDetailed(item.openingBalance)}</td>
                <td>{currencyDetailed(item.amortization)}</td>
                <td>{currencyDetailed(item.interest)}</td>
                <td>{currencyDetailed(item.insurance + item.adminFee)}</td>
                <td>
                  <strong>{currencyDetailed(item.totalPayment)}</strong>
                </td>
                <td>{currencyDetailed(item.closingBalance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
